/**
 * School Mobile App Build Status API Contract
 *
 * GET /v1/schools/:school_id/mobile-app/build-status
 *
 * Which of the school's saved welcome-screen text and welcome-screen photos are
 * not yet in a released build of its app, per platform (plan 2026-08-12-001
 * U12, R16, KTD12). Read by the Custom App Design tab's build-wait strip and by
 * the super-admin readiness panel, which passes the same school gate.
 *
 * What counts as "shipped" is the newest BUILD of the app on that platform
 * whose release target reached `RELEASED`: failed, cancelled, refused and
 * blocked builds never count, a newer build still in flight is named as not yet
 * with families, and an over-the-air update never counts, because a new install
 * runs the bundle the build embedded. Login text and the login photo are never
 * waiting: signed-out families get them from the registry on their second open.
 *
 * AN EXPLICIT ALLOWLIST, NOT A PROJECTION. The build's recorded snapshot carries
 * a git commit, the backend URL it read from, Cloudinary public ids and the
 * resolved branding block; none of it is a school's business and none of it is
 * here. Every field below is assembled by name on the server.
 */

import { z } from 'zod'

export const SCHOOL_APP_BUILD_PLATFORMS = ['ios', 'android'] as const
const SchoolAppBuildPlatformSchema = z.enum(SCHOOL_APP_BUILD_PLATFORMS)
export type SchoolAppBuildPlatform = z.infer<typeof SchoolAppBuildPlatformSchema>

/**
 * The photo slots that ride the build for a new install: the three welcome
 * slides. The login photo (`login-bg`) is not here on purpose; it is live.
 */
export const BUILD_BAKED_PHOTO_SLOTS = ['onboard-1', 'onboard-2', 'onboard-3'] as const
const BuildBakedPhotoSlotSchema = z.enum(BUILD_BAKED_PHOTO_SLOTS)
export type BuildBakedPhotoSlot = z.infer<typeof BuildBakedPhotoSlotSchema>

/**
 * One saved change that is not yet in the released build. `actorLabel` passes
 * through the same masking as the recent-changes list: a person's name only for
 * a member of the school, "School Journey team" for everyone else.
 *
 * A `copy` change names only the welcome-screen fields it touched, in the
 * recent-changes vocabulary (`slides[i].TITLE`, `nextButtonText`, …); what it
 * did to the login text is live and is not listed.
 */
const PendingBuildChangeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('copy'),
    changedAt: z.string().datetime(),
    actorLabel: z.string(),
    changedFields: z.array(z.string())
  }),
  z.object({
    kind: z.literal('photo'),
    changedAt: z.string().datetime(),
    actorLabel: z.string(),
    slot: BuildBakedPhotoSlotSchema
  })
])
export type PendingBuildChange = z.infer<typeof PendingBuildChangeSchema>

/**
 * Why the strip cannot say what the build shipped.
 *
 * - `no_recorded_build` — no released build of the app on this platform has
 *   recorded what it shipped (it predates the snapshot record, or there is no
 *   released build at all).
 * - `build_without_record` — a build ran after the newest recorded one through
 *   an entry point that leaves no build record (a manual workflow run), so what
 *   families have may be newer than anything on file.
 * - `unexplained_difference` — the build baked the registry's text, no change
 *   was saved after it read the text, and the two still differ. Reported rather
 *   than guessed at: a fingerprint drift must never read as "everything is
 *   waiting".
 */
const BUILD_STATUS_CANNOT_TELL_REASONS = [
  'no_recorded_build',
  'build_without_record',
  'unexplained_difference'
] as const

/**
 * What one platform's latest released build shipped, compared with what is
 * saved now.
 *
 * `buildUnderWay` is true when a newer build of the app is in flight on this
 * platform and has not reached families yet; it is reported in every state.
 * `snapshotAt` is when the released build read the school's text, which is the
 * moment "saved after the build" is measured from. `welcomeTextWaiting` says
 * the saved welcome-screen text differs from the build's; `waitingPhotos` are
 * the welcome slides whose photo was replaced after the build. `loginChanged`
 * says the login text or login photo moved since the build, so the strip can
 * say those changes are already live.
 */
const RecordedBuildFields = {
  platform: SchoolAppBuildPlatformSchema,
  buildUnderWay: z.boolean(),
  appVersion: z.string().nullable(),
  snapshotAt: z.string().datetime(),
  welcomeTextWaiting: z.boolean(),
  waitingPhotos: z.array(BuildBakedPhotoSlotSchema),
  loginChanged: z.boolean(),
  pendingChanges: z.array(PendingBuildChangeSchema)
}

const PlatformBuildStatusSchema = z.discriminatedUnion('state', [
  z.object({ ...RecordedBuildFields, state: z.literal('up_to_date') }),
  z.object({ ...RecordedBuildFields, state: z.literal('waiting') }),
  z.object({
    platform: SchoolAppBuildPlatformSchema,
    state: z.literal('cannot_tell'),
    reason: z.enum(BUILD_STATUS_CANNOT_TELL_REASONS),
    buildUnderWay: z.boolean()
  })
])
export type PlatformBuildStatus = z.infer<typeof PlatformBuildStatusSchema>

/** GET response: one entry per platform, iOS first. */
export const SchoolAppBuildStatusResponseSchema = z.object({
  platforms: z.array(PlatformBuildStatusSchema)
})
export type SchoolAppBuildStatusResponse = z.infer<typeof SchoolAppBuildStatusResponseSchema>
